import { Button, Card, DatePicker, Form, Select } from "antd";
import React, { useState } from "react";
import useProviderEwaybillAccountOptions from "../SharedEwaybillAccount/Provider/hooks/useProviderEwaybillAccountOptions";
import moment from "moment";
import { usePagination } from "ahooks";
import {
  getRechargeRecord,
  QuantityChangeOperationType,
  QuantityChangeOperationTypeNameMapping,
} from "@/services/api/rechargeRecord";
import UserType from "@/enum/UserType";
import ShareRechargeRecordDetailTable from "./components/ShareRechargeRecordDetailTable";
import ConsumerSelect from "./components/ConsumerSelect";
import { ShareRechargeRecordDetailProviderFormValues } from "./components/type";

interface ShareRechargeRecordDetailProviderProps {}

const ShareRechargeRecordDetailProvider: React.FC<ShareRechargeRecordDetailProviderProps> = function (props) {
  const [form] = Form.useForm<ShareRechargeRecordDetailProviderFormValues>();
  const [formValues, setFormValues] = useState<ShareRechargeRecordDetailProviderFormValues>(undefined);
  const {
    loading: loading1,
    ewaybillAccountOptions,
    getFilterLogisticsCompanyOptions,
  } = useProviderEwaybillAccountOptions();

  const {
    data,
    loading: loading2,
    pagination,
  } = usePagination(
    async ({ current, pageSize }) => {
      const { rangeDate, consumerCompanyId, ...otherValues } = form.getFieldsValue();

      const startDate = rangeDate?.[0]?.format("YYYY-MM-DD");
      const endDate = rangeDate?.[1]?.format("YYYY-MM-DD");

      const result = await getRechargeRecord({
        ...otherValues,
        consumerCompanyId,
        startDate,
        endDate,
        pageNo: current,
        pageSize,
        userType: UserType.PROVIDER,
        needRecycleRecords: true,
      });

      return {
        list: result.rechargeRecords.content,
        total: result.rechargeRecords.totalElements,
        result,
      };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [formValues],
    }
  );

  return (
    <Card>
      <Form form={form} layout="inline" className="mb10">
        <Form.Item name="rangeDate" label="充值时间">
          <DatePicker.RangePicker
            className="ml16"
            disabledDate={(current) => {
              return (
                current >
                  moment()
                    .add(+1, "days")
                    .endOf("day") || current < moment().add(-180, "days").endOf("day")
              );
            }}
          />
        </Form.Item>

        <Form.Item name="operationType" label="操作类型">
          <Select
            allowClear
            placeholder="全部"
            className="ml16"
            style={{ width: 156 }}
            options={Object.values(QuantityChangeOperationType).map((value) => {
              return {
                value,
                label: QuantityChangeOperationTypeNameMapping[value],
              };
            })}
          ></Select>
        </Form.Item>

        <Form.Item name="ewaybillAccountId" label="我的面单账号" style={{ marginRight: 10 }}>
          <Select
            placeholder="全部"
            style={{ width: 156 }}
            options={ewaybillAccountOptions}
            allowClear
            loading={loading1}
            showSearch
            dropdownMatchSelectWidth={false}
            optionFilterProp="label"
          ></Select>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.ewaybillAccountId !== next.ewaybillAccountId}>
          {(form) => {
            const ewaybillAccountId = form.getFieldValue("ewaybillAccountId");

            return (
              <Form.Item name="logisticsCompany" style={{ marginRight: 10 }}>
                <Select
                  placeholder="全部快递"
                  style={{ width: 156 }}
                  allowClear
                  showSearch
                  options={getFilterLogisticsCompanyOptions(ewaybillAccountId)}
                  optionFilterProp="label"
                ></Select>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item label="使用面单的人" name="consumerCompanyId">
          <ConsumerSelect />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            onClick={() => {
              const values = form.getFieldsValue();

              setFormValues(values);
            }}
          >
            查询
          </Button>
        </Form.Item>

        <Form.Item>
          <Button
            onClick={() => {
              form.resetFields();

              setFormValues(undefined);
            }}
          >
            重置
          </Button>
        </Form.Item>
      </Form>

      <ShareRechargeRecordDetailTable
        rowKey="id"
        loading={loading2}
        result={data?.result}
        dataSource={data?.list}
        userType={UserType.PROVIDER}
        formValues={formValues}
        pagination={{
          ...pagination,
          showQuickJumper: true,
          showTotal: (total) => `总共 ${total} 个项目`,
          showSizeChanger: true,
          pageSizeOptions: ["10", "50", "100", "200"],
        }}
      />
    </Card>
  );
};

export default React.memo(ShareRechargeRecordDetailProvider);
